/* ============================================================
   THE TOKEN THIS PAGE HOLDS, AND HOW IT STAYS FRESH.
   ------------------------------------------------------------
   The sign-in form is ours and posts to our own API, which is
   what exchanges the password for a realm token. So nothing
   here talks to Keycloak — it holds what our API handed back,
   and trades it in before it goes stale.

   A REALM ACCESS TOKEN LASTS FIVE MINUTES. That is deliberate:
   it is the one the server trusts without asking anybody. The
   refresh below is what stops the shortness costing a
   supervisor anything.
   ============================================================ */

const KEY = 'easylearning.auth.v3';

export type Who = {
  sub: string;
  email: string | null;
  name: string | null;
};

export type TokenResponse = {
  token: string;
  refreshToken: string;
  expiresInSeconds: number;
};

type Held = {
  access: string;
  refresh: string;
  /** when the access token stops being accepted, in epoch ms */
  expires: number;
  who: Who;
};

function read(store: Storage): Held | null {
  try {
    const raw = store.getItem(KEY);
    return raw ? (JSON.parse(raw) as Held) : null;
  } catch {
    return null;
  }
}

function load(): { h: Held | null; lasting: boolean } {
  const tab = read(sessionStorage);
  if (tab) return { h: tab, lasting: false };
  const kept = read(localStorage);
  return { h: kept, lasting: kept !== null };
}

const loaded = load();
let held: Held | null = loaded.h;
let lasting = loaded.lasting;

function keep(h: Held | null) {
  held = h;
  try {
    if (h) {
      (lasting ? localStorage : sessionStorage).setItem(KEY, JSON.stringify(h));
      (lasting ? sessionStorage : localStorage).removeItem(KEY);
    } else {
      sessionStorage.removeItem(KEY);
      localStorage.removeItem(KEY);
    }
  } catch { /* storage off */ }
}

const signedOutElsewhere = new Set<() => void>();

export function onSignedOutElsewhere(fn: () => void): () => void {
  signedOutElsewhere.add(fn);
  return () => { signedOutElsewhere.delete(fn); };
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY || e.storageArea !== localStorage || !lasting) return;
    if (e.newValue === null) {
      held = null;
      signedOutElsewhere.forEach((fn) => fn());
    } else {
      held = read(localStorage);
    }
  });
}

/**
 * What the token says about its holder, without trusting it for anything.
 *
 * THE SIGNATURE IS NOT CHECKED HERE AND MUST NOT APPEAR TO BE. This is read to
 * decide what to draw — a name, which screen to show. Everything that matters
 * is decided where the token is verified, which is the API, and a token edited
 * in a console gets a 401 there just the same.
 */
function readClaims(token: string): Who | null {
  try {
    const body = token.split('.')[1];
    const json = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof json.sub !== 'string') return null;
    return {
      sub: json.sub,
      email: typeof json.email === 'string' ? json.email : null,
      name: typeof json.name === 'string' && json.name.trim() ? json.name : null,
    };
  } catch {
    return null;
  }
}

export function setTokens(r: TokenResponse, keepOnDevice?: boolean): boolean {
  const who = readClaims(r.token);
  if (!who) return false;
  if (keepOnDevice !== undefined) lasting = keepOnDevice;
  keep({
    access: r.token,
    refresh: r.refreshToken,
    // a minute early, so a token is never spent in the second it expires
    expires: Date.now() + Math.max(0, (r.expiresInSeconds ?? 300) - 60) * 1000,
    who,
  });
  return true;
}

export const clear = () => keep(null);
export const who = (): Who | null => held?.who ?? null;
export const signedIn = (): boolean => held !== null;
/** The held token, for gating what to draw. Use token() to send one. */
export const currentAccess = (): string | null => held?.access ?? null;

let refreshing: Promise<string | null> | null = null;

/**
 * A token fit to send, or null.
 *
 * ONE REFRESH AT A TIME. A page that renders several panels asks for a token
 * several times at once; without this they would each spend the same refresh
 * token, and Keycloak only honours the first — the rest would sign the
 * supervisor out mid-load for no reason.
 */
export async function token(): Promise<string | null> {
  if (!held) return null;
  if (Date.now() < held.expires) return held.access;
  if (refreshing) return refreshing;

  refreshing = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken: held?.refresh }),
        credentials: 'omit',
      });
      if (!res.ok) { keep(null); return null; }
      const j = (await res.json()) as TokenResponse;
      if (!setTokens(j)) { keep(null); return null; }
      return held?.access ?? null;
    } catch {
      /* A dropped connection is not a bad session. Keep what we have and let
         the call that needed it fail on its own terms. */
      return held?.access ?? null;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}
